import type { Row, RowData } from '@tanstack/react-table';

import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';

import type { AppTableFeatures } from './Table.hooks';

import { useTableContext } from './TableRoot.context';
import { alignmentStyles, cellVisibilityStyles, clickableRowStyle, tableCellStyle, tableRowStyle } from './TableRow.css';

const dialogBackdropSelector = 'div[data-open][role="presentation"][aria-hidden="true"]';

export type Props<TData extends RowData> = {
  // Label for the keyboard/AT trigger. Omit for pointer-only clicks (e.g. row selection handled by a checkbox)
  actionLabel?: string;
  onClick?: (original: TData) => void;
  row: Row<AppTableFeatures, TData>;
};

export const TableRow = <TData extends RowData>({ actionLabel, onClick, row }: Props<TData>) => {
  const table = useTableContext<TData>();

  return (
    <div
      className={clsx(tableRowStyle, !!onClick && clickableRowStyle)}
      onClick={
        onClick &&
        ((event) => {
          // Don't trigger when the interaction originated from a control inside a cell
          if (
            (event.target as HTMLElement).closest(
              `a, button, input, select, textarea, label, [role="button"], [role="menuitem"], ${dialogBackdropSelector}`,
            )
          )
            return;

          onClick(row.original);
        })
      }
      role="row"
    >
      {onClick && actionLabel && (
        // Keyboard/AT trigger for the row action. Visually hidden and out of flow; reveals a row highlight on focus.
        <button className={srOnlyStyle} onClick={() => onClick(row.original)} type="button">
          {actionLabel}
        </button>
      )}

      {row.getVisibleCells().map((cell) => {
        const { meta } = cell.column.columnDef;

        const isMobile = !!meta?.mobile;

        return (
          <div
            className={clsx(
              tableCellStyle,
              !isMobile && alignmentStyles[meta?.styles?.justify ?? 'start'],
              isMobile ? cellVisibilityStyles.mobile : cellVisibilityStyles.desktop,
            )}
            key={cell.id}
            role="cell"
          >
            <table.FlexRender cell={cell} />
          </div>
        );
      })}
    </div>
  );
};
